import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { twMerge } from 'tailwind-merge';
import { TbChevronDown, TbPlus, TbSearch, TbX } from 'react-icons/tb';

import CreateCategoryModal from './CreateCategoryModal';
import { FinancialCategory } from '../../../types/bankAccount';

const SEARCHABLE_THRESHOLD = 8;

interface SimpleCategoryDropdownProps {
  categories: FinancialCategory[];
  value: number | null;
  setValue: (value: number | null) => void;
  onCategoryCreated?: (category: FinancialCategory) => void;
  placeholder?: string;
  className?: string;
  compact?: boolean;
}

export default function SimpleCategoryDropdown({
  categories,
  value,
  setValue,
  onCategoryCreated,
  placeholder,
  className,
  compact = false,
}: SimpleCategoryDropdownProps): JSX.Element {
  const { t } = useTranslation('', { keyPrefix: 'page.financial.categories' });
  const { t: tGeneral } = useTranslation();
  const [open, setOpen] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [search, setSearch] = useState('');
  const wrapperRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const showSearch = categories.length >= SEARCHABLE_THRESHOLD;

  useEffect(() => {
    if (!open) {
      setSearch('');
      return undefined;
    }
    const handler = (e: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    if (showSearch) {
      requestAnimationFrame(() => searchInputRef.current?.focus());
    }
    return () => {
      document.removeEventListener('mousedown', handler);
    };
  }, [open, showSearch]);

  const selected = categories.find((c) => c.financialCategoryId === value);

  const filteredCategories = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return categories;
    return categories.filter((c) => c.name.toLowerCase().includes(q));
  }, [categories, search]);

  return (
    <>
      <div ref={wrapperRef} className={`relative ${className ?? ''}`}>
        <button
          type="button"
          onClick={() => setOpen((o) => !o)}
          className={twMerge(
            'flex w-full items-center justify-between rounded-[8px] border-2 border-secondary-200 bg-secondary-200/50 px-4 text-base font-medium text-primary-900 hover:border-primary-300 focus:border-primary-300 focus:outline-none',
            compact ? 'h-9 px-3 text-sm' : 'h-[50px]',
          )}>
          <span className="flex items-center gap-2 truncate">
            {selected ? (
              <>
                <span
                  className="h-2 w-2 flex-shrink-0 rounded-full"
                  style={{ backgroundColor: selected.color }}
                />
                <span className="truncate">{selected.name}</span>
              </>
            ) : (
              <span className="text-primary-900/70">
                {placeholder ?? t('choosePlaceholder')}
              </span>
            )}
          </span>
          <span className="flex items-center gap-1">
            {selected && (
              <button
                type="button"
                aria-label="Clear"
                onClick={(e) => {
                  e.stopPropagation();
                  setValue(null);
                }}
                className="rounded p-1 hover:bg-secondary-200">
                <TbX className="h-4 w-4" />
              </button>
            )}
            <TbChevronDown className="h-4 w-4" />
          </span>
        </button>
        {open && (
          <div
            role="listbox"
            className="absolute left-0 right-0 top-[calc(100%+4px)] z-50 overflow-hidden rounded-[8px] border-2 border-secondary-200 bg-secondary-50 shadow-lg">
            {showSearch && (
              <div className="sticky top-0 z-10 flex items-center gap-2 border-b border-secondary-200 bg-secondary-50 px-3 py-2">
                <TbSearch className="h-4 w-4 flex-shrink-0 text-primary-900/50" />
                <input
                  ref={searchInputRef}
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder={tGeneral('general.search')}
                  className="w-full border-0 bg-transparent p-0 text-sm font-medium text-primary-900 placeholder:text-primary-900/50 focus:outline-none focus:ring-0"
                  onClick={(e) => e.stopPropagation()}
                  onKeyDown={(e) => e.stopPropagation()}
                />
              </div>
            )}
            <div className="max-h-60 overflow-y-auto py-1">
              {categories.length === 0 ? (
                <div className="px-4 py-2 text-sm text-primary-900/60">
                  {t('emptyState') ?? 'No categories yet'}
                </div>
              ) : filteredCategories.length === 0 ? (
                <div className="px-4 py-2 text-sm text-primary-900/60">
                  {tGeneral('general.noOptions')}
                </div>
              ) : (
                filteredCategories.map((c) => (
                  <button
                    key={c.financialCategoryId}
                    type="button"
                    role="option"
                    aria-selected={value === c.financialCategoryId}
                    onClick={() => {
                      setValue(c.financialCategoryId);
                      setOpen(false);
                    }}
                    className={`flex w-full items-center gap-2 px-4 py-2 text-left text-sm hover:bg-primary-300/30 ${
                      value === c.financialCategoryId ? 'bg-primary/10 font-medium' : ''
                    }`}>
                    <span
                      className="h-2 w-2 flex-shrink-0 rounded-full"
                      style={{ backgroundColor: c.color }}
                    />
                    <span className="truncate">{c.name}</span>
                  </button>
                ))
              )}
              <button
                type="button"
                onClick={() => {
                  setOpen(false);
                  setCreateOpen(true);
                }}
                className="flex w-full items-center gap-2 border-t border-secondary-200 px-4 py-2 text-left text-sm font-medium text-primary-700 hover:bg-primary-300/30">
                <TbPlus className="h-4 w-4" />
                {t('createInlineOption') ?? 'Create new category'}
              </button>
            </div>
          </div>
        )}
      </div>
      <CreateCategoryModal
        open={createOpen}
        setOpen={setCreateOpen}
        onCreated={(c) => {
          setValue(c.financialCategoryId);
          setOpen(false);
          onCategoryCreated?.(c);
        }}
      />
    </>
  );
}